import { DATA_POINTS, TYPES, SCHEMA_ORG, sellerProperty } from './constants';
import { toDollars, toCents } from '../../utils/utilities.mjs';
import { fromGid } from '@/lib/shopify/gid';
import { birchReturnPolicy } from './ReturnPolicySchema';
import { buildShippingByType } from './OfferShippingDetailSchema';

// Shopify's `barcode` is free-text, so only emit a GTIN when it matches the
// GTIN format (8, 12, 13 or 14 digits). Emitted as the generic `gtin` rather
// than `gtin13` because Birch barcodes are 12-digit UPCs. Avoids Google's
// "invalid GTIN" warning on non-GTIN barcodes.
export const asGtin = (barcode) =>
  /^(\d{8}|\d{12,14})$/.test(barcode ?? '') ? barcode : undefined;

export const getVariantUrl = (variant, slug) => {
  const queryParams = variant.selectedOptions?.map((o) => [o.name.toLowerCase(), o.value]) || [];
  const query = new URLSearchParams(queryParams).toString();
  const baseUrl = `${DATA_POINTS.url.product}/${slug}`;
  return query ? `${baseUrl}?${query}` : baseUrl;
};

export function buildOffer(variant, product, variantPrices, slug, siteSettings = {}, options = {}) {
  const {
    includeShipping = false,
    includeReturnPolicy = false,
    includePriceSpec = false,
    includeInventoryLevel = false,
    includeEligibleQty = false,
    includePriceValidUntil = false,
    itemCondition = SCHEMA_ORG.newCondition,
  } = options;

  const currentPrices = variantPrices?.[variant.id] || { sale: 0, full: 0 };
  const activePrice = currentPrices.sale > 0 ? currentPrices.sale : currentPrices.full;
  if (!activePrice) return null;

  const url = getVariantUrl(variant, slug);
  const salePrice = toDollars(toCents(currentPrices.sale));
  const listPrice = toDollars(toCents(currentPrices.full));
  const price = currentPrices.sale > 0 ? salePrice : listPrice;
  const currency = product.currency || DATA_POINTS.locale.currency;
  const variantId = fromGid(variant.id) || variant.id;

  const shippingDetails = includeShipping
    ? (siteSettings.settings?.shipping ?? [])
        .map(s => buildShippingByType(s.type, s))
        .filter(Boolean)
    : [];

  const offer = {
    '@type': TYPES.offer,
    '@id': `${DATA_POINTS.url.product}/${slug}#offer-${variantId}`,
    url,
    ...(variant.sku && { sku: variant.sku }),
    ...(asGtin(variant.barcode) && { gtin: asGtin(variant.barcode) }),
    price,
    priceCurrency: currency,
    ...(includePriceValidUntil && product.priceValidUntil && { priceValidUntil: product.priceValidUntil }),
    availability: variant.availableForSale ? SCHEMA_ORG.inStock : SCHEMA_ORG.outOfStock,
    itemCondition,
    ...sellerProperty,
    ...(includeReturnPolicy && { hasMerchantReturnPolicy: birchReturnPolicy(product?.productType) }),
    ...(includeShipping && shippingDetails.length > 0 && { shippingDetails }),
    ...(includePriceSpec && currentPrices.sale > 0 && {
      priceSpecification: [
        {
          '@type': TYPES.unit_price_specification,
          priceType: SCHEMA_ORG.salePrice,
          price: salePrice,
          priceCurrency: currency,
        },
        {
          '@type': TYPES.unit_price_specification,
          priceType: SCHEMA_ORG.listPrice,
          price: listPrice,
          priceCurrency: currency,
        },
      ],
    }),
    ...(includeInventoryLevel && variant.stockQuantity !== undefined && {
      inventoryLevel: {
        '@type': TYPES.quantitativeValue,
        value: variant.stockQuantity,
      },
    }),
    ...(includeEligibleQty && product.maxOrderQuantity && {
      eligibleQuantity: {
        '@type': TYPES.quantitativeValue,
        minValue: product.minOrderQuantity ?? 1,
        maxValue: product.maxOrderQuantity,
      },
    }),
  };

  return offer;
}

/** @deprecated Use buildOffer with options */
export const buildVariantOffer = (variant, product, variantPrice, slug, siteSettings) =>
  buildOffer(variant, product, { [variant.id]: variantPrice }, slug, siteSettings, {
    includeShipping: true,
    includeReturnPolicy: true,
    includePriceSpec: true,
    itemCondition: SCHEMA_ORG.newCondition,
  });
